import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { fromState } from "@/lib/nav";
import { useBackState, useScrollMemory } from "@/hooks/useScrollMemory";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AuthImg } from "@/components/AuthImg";
import { Input } from "@/components/ui/input";
import { RememberedSearchInput, SearchCameraButton, SearchGo } from "@/components/search/engine";
import { useOwnTag } from "@/components/search/SearchBox";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ListChecks, Search, Truck, PackagePlus, FileSpreadsheet, GitBranch, Boxes, Tag, CheckCircle2, Image, Wrench, SlidersHorizontal, Zap, ShieldCheck, StickyNote, Camera, Box, Unlink } from "lucide-react";

const SOURCE_META = {
  donor: { label: "Donor", icon: Truck, cls: "bg-blue-100 text-blue-700" },
  field: { label: "Field lot", icon: PackagePlus, cls: "bg-amber-100 text-amber-700" },
  import: { label: "Import", icon: FileSpreadsheet, cls: "bg-violet-100 text-violet-700" },
  split: { label: "Split", icon: GitBranch, cls: "bg-teal-100 text-teal-700" },
  manual: { label: "Manual", icon: Boxes, cls: "bg-slate-100 text-slate-600" },
};

const vehicleOf = (p) => {
  const d = p.donor;
  return d ? [d.year, d.make, d.model].filter(Boolean).join(" ") : "";
};

export default function SortQueuePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [sp, setSp] = useSearchParams();
  const setParam = (patch) => setSp((prev) => {
    const n = new URLSearchParams(prev);
    Object.entries(patch).forEach(([k, v]) => {
      if (v == null || v === "" || v === false) n.delete(k);
      else n.set(k, v === true ? "1" : String(v));
    });
    return n;
  }, { replace: true });
  const { has } = useAuth();
  const canGate = has("manage_settings");
  const canManageParts = has("manage_parts");
  const [spawnCap, setSpawnCap] = useState(null);
  const [gate, setGate] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const source = sp.get("source") || "all";
  const q = sp.get("q") || "";
  const sortKey = sp.get("sort") || "tag";     // tag | part | source | vehicle
  const sortDir = sp.get("dir") || "asc";
  const needsWork = sp.get("needs_work") === "1";
  const order = sp.get("order") || "oldest";   // FIFO backlog drain by default
  const bucket = sp.get("bucket") || "ready";  // ready | needs_photo
  const [page, setPage] = useBackState("page", 1);
  const [total, setTotal] = useState(0);
  const [lowerBound, setLowerBound] = useState(false);
  const [stagingTotal, setStagingTotal] = useState(0);
  const [readyTotal, setReadyTotal] = useState(0);
  const [needsPhotoTotal, setNeedsPhotoTotal] = useState(0);
  const sentinelRef = useRef(null);
  const PAGE_SIZE = 50;
  useScrollMemory(!loading);
  // ENTER AND SCANNING (S4, his rule): one of OUR OWN tags opens it — a part in the queue opens here to identify, any
  // other part opens its page, a truck's stock # opens the truck. Everything else typed stays a list.
  const ownTag = useOwnTag("queue-search");
  const onEnter = async (v) => {
    const t = (v || "").trim();
    if (!t) return;
    setParam({ q: t });
    const hit = await ownTag(t);
    if (!hit) return;
    const to = hit.kind === "truck" ? `/donors/${hit.id}` : hit.staging ? `/sort-queue/${hit.id}` : `/parts/${hit.id}`;
    navigate(to, { state: fromState(location, "Identify & Completion") });
  };

  const load = useCallback(async (reset = true) => {
    const nextPage = reset ? 1 : page + 1;
    reset ? setLoading(true) : setLoadingMore(true);
    try {
      const { data } = await api.get("/sort-queue", { params: { source, q, bucket, order, limit: PAGE_SIZE, page: nextPage, box: "sort_identify" } });
      const its = data.items || [];
      setItems((prev) => (reset ? its : [...prev, ...its]));
      setTotal(data.total || 0);
      setLowerBound(!!data.lower_bound);
      setStagingTotal(data.staging_total || 0);
      setReadyTotal(data.ready_total || 0);
      setNeedsPhotoTotal(data.needs_photo_total || 0);
      setPage(data.page || nextPage);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { reset ? setLoading(false) : setLoadingMore(false); }
    // eslint-disable-next-line
  }, [source, q, bucket, order, page]);

  useEffect(() => {
    const t = setTimeout(() => load(true), q.trim() ? 300 : 0);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [source, q, bucket, order]);

  // Infinite scroll — pull the next page when the sentinel enters view.
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const io = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && !loading && !loadingMore && items.length < total) load(false);
    }, { rootMargin: "300px" });
    io.observe(el);
    return () => io.disconnect();
    // eslint-disable-next-line
  }, [loading, loadingMore, items.length, total, source, q, bucket, order, page]);

  useEffect(() => { if (canGate) api.get("/inventory/finalize-gate").then(({ data }) => setGate(data)).catch(() => {}); }, [canGate]);
  const [loadIn, setLoadIn] = useState(null);
  const [readiness, setReadiness] = useState(null);
  useEffect(() => { if (canGate) api.get("/inventory/load-in-mode").then(({ data }) => { setLoadIn(data.enabled); setReadiness(data.readiness); }).catch(() => {}); }, [canGate]);
  const saveLoadIn = async (enabled) => {
    try { const { data } = await api.put("/inventory/load-in-mode", { enabled }); setLoadIn(data.enabled); setReadiness(data.readiness);
      toast.success(enabled ? "Load-In Mode ON — bin overrides allowed (logged) across picking + receiving" : "Load-In Mode OFF — bin scans HARD-enforced everywhere"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const saveGate = async (patch) => {
    try {
      const { data } = await api.put("/inventory/finalize-gate", patch);
      setGate(data);
      toast.success("Finalize gate updated");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  useEffect(() => { if (canManageParts) api.get("/parts/bulk-spawn-config").then(({ data }) => setSpawnCap(data.cap)).catch(() => {}); }, [canManageParts]);
  const saveSpawnCap = async (raw) => {
    const v = Number(raw);
    if (!Number.isInteger(v) || v < 1 || v > 500) { toast.error("Cap must be a whole number from 1 to 500 (can't exceed the 500 tag-batch limit)."); return; }
    if (v === spawnCap) return;
    try { const { data } = await api.post("/parts/bulk-spawn-config", { cap: v }); setSpawnCap(data.cap); toast.success(`Duplicate ×N cap set to ${data.cap}.`); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not update the cap."); }
  };

  const toggleSort = (key) => {
    if (sortKey === key) setParam({ dir: sortDir === "asc" ? "desc" : "asc" });
    else setParam({ sort: key === "tag" ? "" : key, dir: "" });
  };
  const arrow = (key) => (sortKey !== key ? "" : sortDir === "asc" ? " ▲" : " ▼");

  const sorted = useMemo(() => {
    const needsWorkFn = (p) => ((p.numbers || []).length === 0 && !p.oem_number) || !p.name;
    const base = needsWork ? items.filter(needsWorkFn) : items;
    const val = (p) => {
      if (sortKey === "tag") return (p.short_code || "").toLowerCase();
      if (sortKey === "part") return (p.name || "\uffff").toLowerCase();  // unidentified sort last
      if (sortKey === "source") return (p.source_type || "");
      if (sortKey === "vehicle") return (vehicleOf(p) || p.source_label || "").toLowerCase();
      return "";
    };
    const arr = [...base].sort((a, b) => (val(a) < val(b) ? -1 : val(a) > val(b) ? 1 : 0));
    return sortDir === "asc" ? arr : arr.reverse();
  }, [items, sortKey, sortDir, needsWork]);

  const needsWorkCount = useMemo(
    () => items.filter((p) => ((p.numbers || []).length === 0 && !p.oem_number) || !p.name).length,
    [items]);

  const SortBtn = ({ k, children }) => (
    <button data-testid={`sort-${k}`} onClick={() => toggleSort(k)}
      className={`text-[11px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-lg transition-colors ${sortKey === k ? "bg-slate-800 text-white" : "text-slate-500 hover:bg-slate-100"}`}>
      {children}{arrow(k)}
    </button>
  );

  return (
    <div data-testid="sort-queue-page">
      <h1 className="font-display text-2xl font-bold tracking-tight mb-1 flex items-center gap-2"><ListChecks className="w-6 h-6 text-[#B01E28]" />Identify &amp; Completion</h1>
      <p className="text-sm text-slate-500 mb-4">Everything still waiting to be identified — across donors, field lots, imports and splits. Open a part to flesh it out at the desk.</p>

      {canGate && gate && (
        <div data-testid="finalize-gate-control" className="bg-white border border-slate-200 rounded-xl p-4 mb-4">
          <div className="flex items-center gap-2 mb-1"><SlidersHorizontal className="w-4 h-4 text-[#B01E28]" /><span className="text-sm font-bold text-slate-800">Finalize gate (minor parts)</span></div>
          <p className="text-xs text-slate-500 mb-3">Major parts are always fully gated. This only controls minor/bulk parts. Flip anytime — no rebuild.</p>
          <div className="flex flex-wrap items-center gap-2">
            <button data-testid="gate-mode-light" onClick={() => saveGate({ minor_mode: "light" })}
              className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-lg border transition-colors ${gate.minor_mode === "light" ? "bg-[#E0781A] text-white border-[#E0781A]" : "bg-white text-slate-600 border-slate-200 hover:border-[#E0781A]"}`}>
              <Zap className="w-3.5 h-3.5" />Fast (light) — Name + Brand + Value
            </button>
            <button data-testid="gate-mode-gated" onClick={() => saveGate({ minor_mode: "gated" })}
              className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-lg border transition-colors ${gate.minor_mode === "gated" ? "bg-slate-800 text-white border-slate-800" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"}`}>
              <ShieldCheck className="w-3.5 h-3.5" />Gated — also require Grade + Basis
            </button>
            <div className="flex items-center gap-1.5 ml-auto">
              <span className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide">Major backstop ≥ $</span>
              <Input data-testid="gate-threshold" type="number" defaultValue={gate.major_value_threshold ?? ""} placeholder="off"
                onBlur={(e) => { const v = e.target.value === "" ? 0 : Number(e.target.value); if (v !== (gate.major_value_threshold ?? 0)) saveGate({ major_value_threshold: v }); }}
                className="w-24 h-9 rounded-lg font-mono text-sm" />
            </div>
          </div>
        </div>
      )}

      {canManageParts && spawnCap !== null && (
        <div data-testid="bulk-spawn-cap-control" className="bg-white border border-slate-200 rounded-xl p-4 mb-4">
          <div className="flex items-center gap-2 mb-1"><Boxes className="w-4 h-4 text-[#B01E28]" /><span className="text-sm font-bold text-slate-800">Duplicate ×N limit</span></div>
          <p className="text-xs text-slate-500 mb-3">The most parts (and labels) a single &ldquo;Duplicate ×N&rdquo; run can create at once. Keeps an accidental typo from spawning hundreds. 1–500.</p>
          <div className="flex items-center gap-2">
            <span className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide">Max per run</span>
            <Input data-testid="bulk-spawn-cap-input" type="number" min={1} max={500} defaultValue={spawnCap} key={spawnCap}
              onBlur={(e) => saveSpawnCap(e.target.value)}
              className="w-24 h-9 rounded-lg font-mono text-sm" />
          </div>
        </div>
      )}

      {canGate && loadIn !== null && (
        <div data-testid="load-in-mode-control" className="bg-white border border-slate-200 rounded-xl p-4 mb-4">
          <div className="flex items-center gap-2 mb-1"><Box className="w-4 h-4 text-[#B01E28]" /><span className="text-sm font-bold text-slate-800">Load-In Mode <span className="text-[10px] uppercase tracking-wide text-slate-400">· one master switch</span></span></div>
          <p className="text-xs text-slate-500 mb-3">Governs every load-in bin override in one lever — <b>picking</b> and <b>truck / transfer receiving</b>. ON during setup (parts not binned yet): staff can pick &amp; receive without a bin scan, and <b>every override is logged</b> (who · when). Flip OFF once the yards are properly put away — then bin scans are hard-enforced everywhere, with no bypass left open.</p>
          {readiness && (
            <div data-testid="load-in-readiness" className="flex items-center gap-3 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 mb-3">
              <div className="text-2xl font-display font-black tabular-nums" data-testid="load-in-readiness-rate"
                style={{ color: readiness.rate == null ? "#94a3b8" : readiness.rate <= 0.05 ? "#059669" : readiness.rate <= 0.25 ? "#E0781A" : "#B01E28" }}>
                {readiness.rate == null ? "—" : `${Math.round(readiness.rate * 100)}%`}
              </div>
              <div className="text-xs text-slate-500 leading-tight">
                <div className="font-semibold text-slate-700">no-bin override rate · last {readiness.window_days} days</div>
                <div>{readiness.no_bin_total} of {readiness.total} moves skipped the bin ({readiness.no_bin_picks}/{readiness.total_picks} picks · {readiness.no_bin_receives}/{readiness.total_receives} receives)</div>
                <div className="text-[11px] text-slate-400">{readiness.rate == null ? "No picks or receives yet this week." : readiness.rate <= 0.05 ? "Near zero — safe to flip to hard." : "Still relying on the override — keep binning before you flip."}</div>
              </div>
            </div>
          )}
          <div className="flex flex-wrap items-center gap-2">
            <button data-testid="load-in-on" onClick={() => saveLoadIn(true)}
              className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-lg border transition-colors ${loadIn ? "bg-[#E0781A] text-white border-[#E0781A]" : "bg-white text-slate-600 border-slate-200 hover:border-[#E0781A]"}`}>
              <Unlink className="w-3.5 h-3.5" />Load-In ON — overrides allowed (logged)
            </button>
            <button data-testid="load-in-off" onClick={() => saveLoadIn(false)}
              className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-lg border transition-colors ${!loadIn ? "bg-slate-800 text-white border-slate-800" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"}`}>
              <ShieldCheck className="w-3.5 h-3.5" />Running properly — hard-enforce bins
            </button>
          </div>
        </div>
      )}

      <div className="flex items-center gap-2 mb-3 flex-wrap" data-testid="queue-buckets">
        <button data-testid="queue-bucket-ready" onClick={() => setParam({ bucket: "" })}
          className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-lg border transition-colors ${bucket === "ready" ? "bg-slate-800 text-white border-slate-800" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"}`}>
          <ListChecks className="w-3.5 h-3.5" />Ready to identify ({readyTotal})
        </button>
        <button data-testid="queue-bucket-needs-photo" onClick={() => setParam({ bucket: "needs_photo" })}
          className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-lg border transition-colors ${bucket === "needs_photo" ? "bg-[#B45309] text-white border-[#B45309]" : "bg-white text-slate-600 border-slate-200 hover:border-amber-400"}`}>
          <Camera className="w-3.5 h-3.5" />Needs photos ({needsPhotoTotal})
        </button>
        <span className="text-[11px] text-slate-400 font-mono ml-auto">{stagingTotal} total in staging</span>
      </div>

      {bucket === "needs_photo" && (
        <div data-testid="queue-needs-photo-note" className="rounded-xl border border-amber-200 bg-amber-50 p-3 mb-3 text-[13px] text-amber-800 flex gap-2">
          <Camera className="w-4 h-4 mt-0.5 shrink-0" />
          <span>These staging parts are tagged and located but have <b>no photo yet</b>, so they can't be identified until a photo is added. Open one to add photos — it then moves to <b>Ready to identify</b>.</span>
        </div>
      )}

      <div className="flex flex-col sm:flex-row gap-2 mb-3">
        <div className="flex items-center gap-1.5 flex-1">
          <div className="relative flex-1">
            <Search className="w-4 h-4 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
            <RememberedSearchInput testid="queue-search" value={q} onChange={(v) => setParam({ q: v })} placeholder="Search tag, name, part #, truck, lot…" className="pl-8 rounded-lg"
              enterKeyHint="search" onKeyDown={(e) => { if (e.key === "Enter") onEnter(e.currentTarget.value); }} />
          </div>
          {/* S5: Enter or this button runs the search — never while typing */}
          <SearchGo testid="queue-search" onGo={(v) => { setParam({ q: v }); onEnter(v); }} size="h-10" />
          <SearchCameraButton testid="queue-search" onScan={onEnter} />
        </div>
        <Select value={source} onValueChange={(v) => setParam({ source: v === "all" ? "" : v })}>
          <SelectTrigger data-testid="queue-source-filter" className="rounded-lg sm:w-48"><SelectValue /></SelectTrigger>
          <SelectContent className="rounded-lg">
            <SelectItem value="all">All sources</SelectItem>
            <SelectItem value="field">Field lots</SelectItem>
            <SelectItem value="donor">Donors</SelectItem>
            <SelectItem value="import">Imports</SelectItem>
            <SelectItem value="split">Splits</SelectItem>
            <SelectItem value="manual">Manual</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Sort + order bar */}
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        {bucket === "ready" && (
          <button data-testid="queue-filter-needs-work" onClick={() => setParam({ needs_work: !needsWork })}
            className={`inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full border transition-colors ${needsWork ? "bg-[#B01E28] text-white border-[#B01E28]" : "bg-white text-slate-500 border-slate-200 hover:border-[#B01E28]"}`}>
            <Wrench className="w-3 h-3" />Needs work ({needsWorkCount})
          </button>
        )}
        <span className="text-[11px] text-slate-400 font-semibold uppercase tracking-wider">Order:</span>
        <button data-testid="queue-order-oldest" onClick={() => setParam({ order: "" })}
          className={`text-[11px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-lg transition-colors ${order === "oldest" ? "bg-slate-800 text-white" : "text-slate-500 hover:bg-slate-100"}`}>Oldest first (FIFO)</button>
        <button data-testid="queue-order-newest" onClick={() => setParam({ order: "newest" })}
          className={`text-[11px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-lg transition-colors ${order === "newest" ? "bg-slate-800 text-white" : "text-slate-500 hover:bg-slate-100"}`}>Newest</button>
        <span className="w-px h-4 bg-slate-200 mx-1" />
        <span className="text-[11px] text-slate-400 font-semibold uppercase tracking-wider">Sort:</span>
        <SortBtn k="tag">Tag</SortBtn>
        <SortBtn k="part">Part</SortBtn>
        <SortBtn k="vehicle">Vehicle</SortBtn>
        <SortBtn k="source">Source</SortBtn>
        <span data-testid="queue-result-count" className="text-xs text-slate-400 font-mono ml-auto">Showing {sorted.length} of {total}{lowerBound ? "+" : ""}</span>
      </div>

      {loading ? (
        <div className="p-10 text-center font-mono text-xs text-slate-400">LOADING QUEUE…</div>
      ) : sorted.length === 0 ? (
        <div className="border border-dashed border-slate-200 rounded-xl p-12 text-center" data-testid="queue-empty">
          {bucket === "needs_photo" ? (
            <><Camera className="w-8 h-8 mx-auto text-amber-300 mb-2" /><p className="text-sm text-slate-500">No parts are waiting on photos — every staging part has at least one.</p></>
          ) : (
            <><CheckCircle2 className="w-8 h-8 mx-auto text-emerald-300 mb-2" /><p className="text-sm text-slate-500">Nothing ready to identify here. {needsPhotoTotal > 0 ? `${needsPhotoTotal} part(s) are waiting on photos — see the "Needs photos" tab.` : "Nice — the queue is clear."}</p></>
          )}
        </div>
      ) : (
        <div className="space-y-2" data-testid="queue-list">
          {sorted.map((p) => {
            const meta = SOURCE_META[p.source_type] || SOURCE_META.manual;
            const Icon = meta.icon;
            const vehicle = vehicleOf(p);
            const d = p.donor;
            return (
              <button key={p.id} data-testid={`queue-item-${p.short_code}`} data-back-row onClick={() => navigate(`/sort-queue/${p.id}`, { state: fromState(location, "Identify & Completion") })}
                className="w-full text-left border border-slate-200 bg-white rounded-xl p-3.5 hover:border-[#E0781A] hover:shadow-sm transition-all flex items-center gap-3">
                <div className="w-14 h-14 rounded-lg border border-slate-200 overflow-hidden bg-slate-50 shrink-0 flex items-center justify-center relative">
                  {p.photo_count > 0 && p.photos?.[0]?.file_id ? <AuthImg fileId={p.photos[0].file_id} thumb missing={!!p.photos[0].bytes_missing} className="w-full h-full object-cover" /> : <Tag className="w-5 h-5 text-slate-300" />}
                  {p.photo_count > 1 && <span className="absolute bottom-0.5 right-0.5 inline-flex items-center gap-0.5 text-[9px] font-mono px-1 rounded bg-slate-900/70 text-white"><Image className="w-2.5 h-2.5" />{p.photo_count}</span>}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono font-semibold text-slate-800">{p.short_code}</span>
                    <span className="text-sm text-slate-600 truncate">{p.name || <span className="italic text-slate-400">Unidentified — open to identify</span>}</span>
                    {p.lot_has_notes && <span data-testid={`queue-lot-notes-${p.short_code}`} title="This lot carries dismantler notes" className="inline-flex items-center gap-1 text-[10px] font-semibold text-[#B45309] bg-amber-100 rounded-full px-1.5 py-0.5"><StickyNote className="w-3 h-3" />Lot notes</span>}
                  </div>
                  <div data-testid={`queue-item-vehicle-${p.short_code}`} className="text-xs text-slate-500 mt-1 flex flex-wrap items-center gap-x-2.5 gap-y-0.5">
                    {vehicle && <span className="font-semibold text-slate-600">{vehicle}</span>}
                    {d?.stock_number && <span>Stock <span className="font-mono">{d.stock_number}</span></span>}
                    {d?.vin && <span className="font-mono text-slate-400">VIN {d.vin}</span>}
                    {!d && <span className="font-mono text-slate-400">{p.source_label}</span>}
                  </div>
                </div>
                {bucket === "needs_photo" && <Badge data-testid={`queue-needs-photo-${p.short_code}`} className="rounded-full border-0 text-[10px] font-mono shrink-0 bg-amber-100 text-amber-700"><Camera className="w-3 h-3 mr-1" />Add photos</Badge>}
                <Badge className={`rounded-full border-0 text-[10px] font-mono shrink-0 ${meta.cls}`}><Icon className="w-3 h-3 mr-1" />{meta.label}</Badge>
              </button>
            );
          })}
          <div ref={sentinelRef} className="h-1" data-testid="queue-sentinel" />
        </div>
      )}
      {!loading && items.length < total && (
        <div className="flex justify-center mt-4">
          <Button data-testid="queue-load-more" variant="outline" disabled={loadingMore} onClick={() => load(false)} className="rounded-lg">
            {loadingMore ? "Loading…" : `Load more (${total - items.length} more)`}
          </Button>
        </div>
      )}
    </div>
  );
}
